import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, CheckCircle2, ShieldAlert, Upload, XCircle } from "lucide-react";
import { useRef, useState } from "react";

import {
  getEstadoCredenciales,
  guardarCredenciales,
  type EstadoCredenciales,
  type FielCertificado,
} from "@/lib/api";
import { usePermisos } from "@/lib/authz";
import { BADGE_VIGENCIA, DIAS_AVISO, estadoVigencia, fmtFecha } from "@/lib/fiel";

export function CredencialesPage() {
  const { me } = usePermisos();
  const estadoQ = useQuery({
    queryKey: ["cenace-credenciales"],
    queryFn: getEstadoCredenciales,
  });

  const [simUsuario, setSimUsuario] = useState("");
  const [simClave, setSimClave] = useState("");
  const [fielPassword, setFielPassword] = useState("");
  const cerRef = useRef<HTMLInputElement>(null);
  const keyRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const guardar = async () => {
    const fd = new FormData();
    if (simUsuario) fd.append("sim_usuario", simUsuario);
    if (simClave) fd.append("sim_clave", simClave);
    if (fielPassword) fd.append("fiel_password", fielPassword);
    const cer = cerRef.current?.files?.[0];
    const key = keyRef.current?.files?.[0];
    if (cer) fd.append("fiel_cer", cer);
    if (key) fd.append("fiel_key", key);

    if (![...fd.keys()].length) {
      setError("Llena al menos un campo.");
      return;
    }
    setBusy(true);
    setMsg(null);
    setError(null);
    try {
      const r = await guardarCredenciales(fd);
      setMsg(`Guardado en Secret Manager: ${r.guardados.join(", ")}`);
      setSimUsuario("");
      setSimClave("");
      setFielPassword("");
      if (cerRef.current) cerRef.current.value = "";
      if (keyRef.current) keyRef.current.value = "";
      estadoQ.refetch();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Mis credenciales CENACE</h1>
        <p className="text-sm text-slate-500 mt-1">
          Certificado FIEL (e.firma) y acceso al SIM para el envío de TBFin. Son
          <span className="font-medium"> personales</span>: cada usuario sube las suyas y
          se usan con su cuenta. Se guardan en Google Secret Manager — nunca en la base de
          datos ni en texto plano.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Ligadas a tu cuenta <span className="font-medium">{me?.email}</span>. Datos
          sensibles: solo se suben (no se descargan ni se muestran). Cada guardado crea una
          versión nueva del secret.
        </span>
      </div>

      {/* Estado actual */}
      <div className="bg-white border rounded-lg p-4">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Estado actual</h2>
        {estadoQ.isLoading && <div className="text-sm text-slate-500">Cargando…</div>}
        {estadoQ.isError && (
          <div className="text-sm text-red-600">{(estadoQ.error as Error).message}</div>
        )}
        {estadoQ.data && <EstadoLista estado={estadoQ.data} />}
        {estadoQ.data?.fiel && <FichaCertificado fiel={estadoQ.data.fiel} />}
      </div>

      {/* Formulario */}
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <h2 className="text-sm font-semibold text-slate-800">Cargar / actualizar</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Certificado FIEL (.cer)">
            <input ref={cerRef} type="file" accept=".cer" className="text-sm w-full" />
          </Field>
          <Field label="Llave privada FIEL (.key)">
            <input ref={keyRef} type="file" accept=".key" className="text-sm w-full" />
          </Field>
          <Field label="Contraseña de la llave FIEL">
            <input
              type="password"
              value={fielPassword}
              onChange={(e) => setFielPassword(e.target.value)}
              className="border rounded px-2 py-1.5 w-full text-sm"
              autoComplete="new-password"
            />
          </Field>
          <div />
          <Field label="Usuario SIM">
            <input
              type="text"
              value={simUsuario}
              onChange={(e) => setSimUsuario(e.target.value)}
              className="border rounded px-2 py-1.5 w-full text-sm"
              autoComplete="off"
            />
          </Field>
          <Field label="Contraseña SIM">
            <input
              type="password"
              value={simClave}
              onChange={(e) => setSimClave(e.target.value)}
              className="border rounded px-2 py-1.5 w-full text-sm"
              autoComplete="new-password"
            />
          </Field>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={guardar}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" /> {busy ? "Guardando…" : "Guardar en Secret Manager"}
          </button>
          {msg && <span className="text-sm text-emerald-600">{msg}</span>}
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>
      </div>
    </div>
  );
}

type ClaveBooleana = Exclude<keyof EstadoCredenciales, "fiel">;

function EstadoLista({ estado }: { estado: EstadoCredenciales }) {
  const items: { key: ClaveBooleana; label: string }[] = [
    { key: "fiel_cer", label: "Certificado FIEL (.cer)" },
    { key: "fiel_key", label: "Llave privada FIEL (.key)" },
    { key: "fiel_password", label: "Contraseña FIEL" },
    { key: "sim_usuario", label: "Usuario SIM" },
    { key: "sim_clave", label: "Contraseña SIM" },
  ];
  return (
    <ul className="space-y-1.5 text-sm">
      {items.map((it) => (
        <li key={it.key} className="flex items-center gap-2">
          {estado[it.key] ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          ) : (
            <XCircle className="w-4 h-4 text-slate-300" />
          )}
          <span className={estado[it.key] ? "text-slate-800" : "text-slate-400"}>
            {it.label} — {estado[it.key] ? "configurado" : "pendiente"}
            {it.key === "fiel_cer" && estado.fiel?.vence && (
              <span className="text-slate-500">
                {", vence el "}
                <span className="font-medium text-slate-700">{fmtFecha(estado.fiel.vence)}</span>
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Ficha del .cer cargado: sobre todo CUÁNDO VENCE, que es el dato que hace
 * fallar el envío al SIM sin avisar. Todo lo que se muestra es público (viene
 * del certificado, no de la llave privada).
 */
function FichaCertificado({ fiel }: { fiel: FielCertificado }) {
  if (fiel.error) {
    return (
      <div className="mt-3 pt-3 border-t flex items-start gap-2 text-sm text-red-600">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{fiel.error} Vuelve a subir el archivo .cer de tu e.firma.</span>
      </div>
    );
  }
  const v = estadoVigencia(fiel);
  const BADGE = BADGE_VIGENCIA[v.tono];

  return (
    <div className="mt-3 pt-3 border-t space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarClock className="w-4 h-4 text-slate-400" />
        <span className="text-sm text-slate-600">Vigencia del certificado:</span>
        <span className="text-sm text-slate-800">
          {fiel.desde && fmtFecha(fiel.desde)}
          <span className="text-slate-400 mx-1.5">→</span>
          <b>{fiel.vence && fmtFecha(fiel.vence)}</b>
        </span>
        <span className={`text-[11px] rounded-full border px-2 py-0.5 ${BADGE}`}>{v.texto}</span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-slate-600">
        {fiel.titular && (<><dt className="text-slate-400">Titular</dt><dd>{fiel.titular}</dd></>)}
        {fiel.rfc && (<><dt className="text-slate-400">RFC</dt><dd className="font-mono">{fiel.rfc}</dd></>)}
        {fiel.serie && (<><dt className="text-slate-400">Nº de serie</dt><dd className="font-mono">{fiel.serie}</dd></>)}
        {fiel.emisor && (<><dt className="text-slate-400">Emisor</dt><dd>{fiel.emisor}</dd></>)}
      </dl>
      {fiel.vencido && (
        <div className="flex items-start gap-2 text-sm text-red-600">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            La e.firma está vencida: el envío de TBFin al SIM va a fallar. Renuévala en el
            SAT y sube el .cer y la .key nuevos.
          </span>
        </div>
      )}
      {!fiel.vencido && (fiel.dias_restantes ?? 999) <= DIAS_AVISO && (
        <div className="flex items-start gap-2 text-sm text-amber-700">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Conviene renovarla antes de que venza: cuando expire, el envío al SIM dejará de
            funcionar.
          </span>
        </div>
      )}
      {fiel.aun_no_vigente && (
        <div className="text-sm text-amber-700">
          El certificado todavía no entra en vigor ({fiel.desde && fmtFecha(fiel.desde)}).
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
